import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

import { API_BASE_URL } from '../config/api';

const BACKEND_URL = API_BASE_URL;

const TICKER_INSTRUMENTS = [
  { symbol: 'EUR/USD', type: 'forex', key: 'EURUSD' },
  { symbol: 'GBP/USD', type: 'forex', key: 'GBPUSD' },
  { symbol: 'USD/JPY', type: 'forex', key: 'USDJPY' },
  { symbol: 'BTC/USD', type: 'crypto', key: 'BTCUSDT' },
  { symbol: 'ETH/USD', type: 'crypto', key: 'ETHUSDT' },
  { symbol: 'XAU/USD', type: 'commodity', key: 'GOLD' },
  { symbol: 'S&P 500', type: 'index', key: 'SPX' },
  { symbol: 'NASDAQ', type: 'index', key: 'IXIC' },
  { symbol: 'DOW', type: 'index', key: 'DJI' },
  { symbol: 'OIL', type: 'commodity', key: 'OIL' },
];

const formatTickerPrice = (price, type) => {
  if (type === 'forex') return price.toFixed(4);
  if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return price.toFixed(2);
};

export const MarketTicker = () => {
  const [tickerData, setTickerData] = useState([]);

  useEffect(() => {
    const fetchWithTimeout = async (url) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timer);
        return await res.json();
      } catch {
        clearTimeout(timer);
        return null;
      }
    };

    const fetchTickerData = async () => {
      try {
        const [cryptoRes, forexRes, indicesRes, commoditiesRes] = await Promise.all([
          fetchWithTimeout(`${BACKEND_URL}/api/crypto-prices`),
          fetchWithTimeout(`${BACKEND_URL}/api/forex-prices`),
          fetchWithTimeout(`${BACKEND_URL}/api/indices-prices`),
          fetchWithTimeout(`${BACKEND_URL}/api/commodities-prices`),
        ]);

        const crypto = cryptoRes?.success ? cryptoRes.prices : {};
        const forex = forexRes?.success ? forexRes.prices : {};
        const indices = indicesRes?.success ? indicesRes.prices : {};
        const commodities = commoditiesRes?.success ? commoditiesRes.prices : {};

        const items = TICKER_INSTRUMENTS.map(inst => {
          if (inst.type === 'forex' && forex[inst.key]) {
            return { symbol: inst.symbol, price: formatTickerPrice(forex[inst.key].price, 'forex'), change: 0 };
          }
          if (inst.type === 'crypto' && crypto[inst.key]) {
            const d = crypto[inst.key];
            return { symbol: inst.symbol, price: formatTickerPrice(d.price, 'crypto'), change: d.changePercent };
          }
          if (inst.type === 'index' && indices[inst.key]) {
            const d = indices[inst.key];
            return { symbol: inst.symbol, price: formatTickerPrice(d.price, 'index'), change: d.changePercent };
          }
          if (inst.type === 'commodity' && commodities[inst.key]) {
            const d = commodities[inst.key];
            return { symbol: inst.symbol, price: formatTickerPrice(d.price, 'commodity'), change: d.changePercent };
          }
          return null;
        }).filter(Boolean);

        setTickerData(items);
      } catch (e) {
        console.error('Ticker fetch error:', e);
      }
    };

    fetchTickerData();
    const interval = setInterval(fetchTickerData, 15000);
    return () => clearInterval(interval);
  }, []);

  if (tickerData.length === 0) return null;

  const doubled = [...tickerData, ...tickerData];

  return (
    <div className="bg-gray-950 text-white overflow-hidden h-9 flex items-center relative z-[60]" data-testid="market-ticker">
      <div className="ticker-track flex items-center gap-8 whitespace-nowrap">
        {doubled.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-xs font-medium shrink-0">
            <span className="text-gray-400 font-semibold">{item.symbol}</span>
            <span className="text-white font-mono">{item.price}</span>
            {item.change !== 0 && (
              <span className={`flex items-center gap-0.5 ${item.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {item.change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MarketTicker;
